import { clsx } from 'clsx';
import type { MouseEvent } from 'react';
import { memo } from 'react';
import { slowCn } from '../../lib/utils';
import { useSlot } from '../../slots';
import type { IconComponentType } from '../../types/index';
import { isNative } from '../../utils/isNative';
import { Box } from '../Box';
import type { TooltipProps } from '../Tooltip';
import { Tooltip } from '../Tooltip';
import { ListItem as BaseListItem } from './Components/ListItem';
import MenuContent from './MenuContent';
import { menuItemHelpTextVariants, menuItemIconVariants, menuItemTitleVariants } from './styles';

export enum MenuItemHelpTextPosition {
  Inline = 'inline',
  InTooltip = 'tooltip',
}
export type MenuItemProps = {
  startDecoratorComponent?: IconComponentType;
  startDecoratorNode?: React.ReactNode;
  endDecoratorComponent?: IconComponentType;
  endDecoratorNode?: React.ReactNode;
  title: string;
  caption?: string;
  helpTextDetails?: {
    text: string | React.ReactNode;
    position: MenuItemHelpTextPosition;
    placement?: TooltipProps['placement'];
  };
  className?: string;
  onClick?: (event: MouseEvent) => void;
  onMouseOver?: (event: MouseEvent) => void;
  onMouseEnter?: (event: MouseEvent) => void;
  variant?: 'default' | 'danger';
  disabled?: boolean;
  selected?: boolean;
  size?: 'sm' | 'md';
  captionClassName?: string;
  titleClassName?: string;
  menuContentContainerClassName?: string;
  slots?: {
    menuContentItem?: React.ElementType;
  };
  onMouseDown?: (event: MouseEvent) => void;
  index?: number;
  enableHoverEffect?: boolean;
  id?: string;
  iconClassName?: string;
};

const MenuItem = memo(function MenuItem(props: MenuItemProps) {
  const {
    title,
    helpTextDetails,
    className,
    menuContentContainerClassName,
    onClick,
    onMouseOver,
    onMouseEnter,
    variant = 'default',
    startDecoratorComponent: StartDecoratorComponent,
    startDecoratorNode,
    endDecoratorComponent: EndDecoratorComponent,
    endDecoratorNode,
    disabled = false,
    selected,
    size = 'md',
    slots,
    caption,
    onMouseDown,
    titleClassName: _titleClassName,
    captionClassName,
    index,
    enableHoverEffect = true,
    id,
    iconClassName: _iconClassName,
  } = props;

  const iconClassName = menuItemIconVariants({ variant, disabled, className: _iconClassName });
  const titleClassName = menuItemTitleVariants({ variant, disabled, className: _titleClassName });
  const helpTextClassName = menuItemHelpTextVariants({ variant, disabled });

  const startDecorator = StartDecoratorComponent ? (
    <StartDecoratorComponent className={iconClassName} height={16} width={16} />
  ) : (
    startDecoratorNode
  );

  const endDecorator = EndDecoratorComponent ? (
    <EndDecoratorComponent
      className={clsx('justify-self-end', iconClassName)}
      height={16}
      width={16}
    />
  ) : (
    endDecoratorNode
  );

  const [MenuContentItem, menuContentItemProps] = useSlot('menuContentItem', {
    elementType: MenuContent,
    className: slowCn(
      'row bg-primary my-xxs mx-sm ps-md pe-md flex flex-row items-center rounded-sm',
      { 'cursor-pointer': onClick },
      { 'hover:bg-secondary_hover': enableHoverEffect && !isNative() },
      { 'pointer-events-none': disabled },
      { 'bg-quaternary': selected },
      { 'py-md !h-9 is-platform:py-sm is-platform:!h-8 is-platform:!min-h-8': size === 'md' },
      { 'py-sm !h-7 !min-h-7': size === 'sm' },
      className,
    ),
    additionalProps: {
      startDecorator,
      endDecorator,
      title,
      titleClassName,
      captionClassName,
      menuContentContainerClassName,
      helpTextDetails,
      helpTextClassName,
      caption,
      size,
      onClick,
      onMouseOver,
      onMouseEnter,
      disabled,
      selected,
      className,
      onMouseDown,
      index,
      id,
    },
    externalForwardedProps: { slots },
    ownerState: {},
  });

  const el = <MenuContentItem {...menuContentItemProps} />;

  if (helpTextDetails?.position === MenuItemHelpTextPosition.InTooltip) {
    return (
      <Tooltip
        arrow={false}
        offset={6}
        placement={helpTextDetails.placement}
        title={helpTextDetails.text}
      >
        <Box className="h-full w-full">{el}</Box>
      </Tooltip>
    );
  }

  return el;
});

export { BaseListItem };
export default MenuItem;
